import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Send cookies on cross-origin requests so Google session_token cookie travels with /auth/me calls.
axios.defaults.withCredentials = true;

const setAuthHeader = (token) => {
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete axios.defaults.headers.common["Authorization"];
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("sigortapro-token"));
  const [loading, setLoading] = useState(true);
  // Process Google OAuth callback (session_id in URL fragment) once per mount.
  // Reference: hasProcessed is a ref so StrictMode double-render doesn't double-exchange.
  const hasProcessedCallback = useRef(false);
  // When login/register sets user data directly, skip the next /auth/me re-check
  // so a stale session_token cookie cannot overwrite the fresh authoritative response.
  const skipNextMeCheck = useRef(false);

  useEffect(() => {
    (async () => {
      // 1) OAuth callback: look for #session_id=... in hash BEFORE normal auth check.
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const hash = typeof window !== "undefined" ? window.location.hash : "";
      if (hash.includes("session_id=") && !hasProcessedCallback.current) {
        hasProcessedCallback.current = true;
        const match = hash.match(/session_id=([^&]+)/);
        const sessionId = match ? decodeURIComponent(match[1]) : "";
        if (sessionId) {
          try {
            const { data } = await axios.post(`${API}/auth/google-session`, { sessionId });
            localStorage.setItem("sigortapro-token", data.sessionToken);
            setAuthHeader(data.sessionToken);
            skipNextMeCheck.current = true;
            setToken(data.sessionToken);
            setUser(data.user);
            try {
              window.history.replaceState(null, "", window.location.pathname + window.location.search);
            } catch {}
          } catch (err) {
            console.error("Google session exchange failed:", err?.response?.data || err?.message);
          }
        }
      }
      // 2) Normal path: validate existing JWT/session_token with backend.
      if (!token) {
        setLoading(false);
        return;
      }
      setAuthHeader(token);
      // Skip the re-check if login/register/google-callback just populated the user data.
      // This prevents a stale session_token cookie from downgrading an admin role via /auth/me.
      if (skipNextMeCheck.current) {
        skipNextMeCheck.current = false;
        setLoading(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        setUser(data);
      } catch (e) {
        localStorage.removeItem("sigortapro-token");
        setToken(null);
        setAuthHeader(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    // Nuke any stale session_token cookie from a prior Google test so it can't shadow this JWT.
    try { await axios.post(`${API}/auth/logout`); } catch {}
    localStorage.setItem("sigortapro-token", data.token);
    setAuthHeader(data.token);
    skipNextMeCheck.current = true;
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password });
    try { await axios.post(`${API}/auth/logout`); } catch {}
    localStorage.setItem("sigortapro-token", data.token);
    setAuthHeader(data.token);
    skipNextMeCheck.current = true;
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    localStorage.removeItem("sigortapro-token");
    setAuthHeader(null);
    setToken(null);
    setUser(null);
  };

  const setSessionFromReset = (newToken, newUser) => {
    localStorage.setItem("sigortapro-token", newToken);
    setAuthHeader(newToken);
    setToken(newToken);
    setUser(newUser);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, loginWithGoogle, logout, setSessionFromReset, refreshUser: async () => {
      try { const { data } = await axios.get(`${API}/auth/me`); setUser(data); } catch (e) {}
    } }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Check if the current user has a named permission. Admin always returns true.
export function useHasPermission(name) {
  const { user } = useAuth() || {};
  if (!user) return false;
  if (user.role === "admin") return true;
  return !!(user.permissions || {})[name];
}

export const isAdmin = (user) => user?.role === "admin";

export function formatApiError(detail, fallback = "Bir hata oluştu.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ") || fallback;
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
